import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  useTheme,
  InputAdornment,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { tokens } from "../../theme";

/* ---------- helpers ---------- */
const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
};
const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

// Convert ISO -> "YYYY-MM-DDTHH:mm" (for <input type="datetime-local">)
const toDateTimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${hh}:${mm}`;
};

// Convert "YYYY-MM-DDTHH:mm" (local) -> ISO string
const fromDateTimeLocalToISO = (local) => {
  if (!local) return "";
  const d = new Date(local);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
};

// ensure numeric ID for consistent select values
const toNum = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // e.g. https://example.com/api
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewCarOperation() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    actionType: "fuel",
    carId: "",
    userId: "",
    currentMileage: "",
    previousMileage: "",
    previousFuel: "",
    currentFuel: "",
    serviceCost: "",
    serviceDate: "",
    assignDate: "",
  });
  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // dropdown options
  const [users, setUsers] = React.useState([]);
  const [cars, setCars] = React.useState([]);
  const [optsLoading, setOptsLoading] = React.useState(false);

  // fetch list
  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/car-operation");
    setLoading(false);
    if (error) {
      setToast({
        open: true,
        msg: "Failed to load car operations",
        sev: "error",
      });
      setRows([]);
      return;
    }
    setRows(Array.isArray(data) ? data : []);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // fetch options for dropdowns (users + cars)
  const loadOptions = React.useCallback(async () => {
    setOptsLoading(true);
    const [uRes, cRes] = await Promise.all([
      getJSON("/api/user"),
      getJSON("/api/carInfo"),
    ]);
    setOptsLoading(false);
    setUsers(Array.isArray(uRes.data) ? uRes.data : []);
    setCars(Array.isArray(cRes.data) ? cRes.data : []);
  }, []);

  React.useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // keep current ID selectable even if not in fetched list
  function ensureOption(options, id) {
    if (id === "" || id === null || id === undefined) return options || [];
    const nid = Number(id);
    const exists = (options || []).some((o) => Number(o.id) === nid);
    return exists
      ? options
      : [
          { id: nid, fullName: `#${nid}`, carName: `#${nid}` },
          ...(options || []),
        ];
  }

  // label helpers based on loading/empty
  const userLabel = optsLoading
    ? "Loading users..."
    : users.length
    ? "User (optional)"
    : "No users found";
  const carLabel = optsLoading
    ? "Loading cars..."
    : cars.length
    ? "Car"
    : "No cars found";

  // columns
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 80, hide: true },
    { field: "actionType", headerName: "Action", width: 110 },
    {
      field: "car",
      headerName: "Car",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => {
        const c = params.row.car;
        if (!c) return "-";
        const name = c.carName ?? c.model ?? c.title ?? "-";
        const plate = c.carPlate ?? c.plateNumber ?? c.plate ?? "";
        return plate ? `${name} (${plate})` : name;
      },
    },
    {
      field: "user",
      headerName: "User",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.row.User?.fullName ?? "-",
    },
    {
      field: "currentMileage",
      headerName: "Curr. Mileage",
      width: 130,
      valueGetter: (p) => p.row.currentMileage ?? "-",
    },
    {
      field: "previousMileage",
      headerName: "Prev. Mileage",
      width: 130,
      valueGetter: (p) => p.row.previousMileage ?? "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={async () => {
                  setEditRow(row);
                  setEditValues({
                    actionType: row.actionType ?? "fuel",
                    carId: toNum(row.carId),
                    userId: row.userId == null ? "" : toNum(row.userId),
                    currentMileage: row.currentMileage ?? "",
                    previousMileage: row.previousMileage ?? "",
                    previousFuel: row.previousFuel ?? "",
                    currentFuel: row.currentFuel ?? "",
                    serviceCost: row.serviceCost ?? "",
                    serviceDate: toDateTimeLocal(row.serviceDate),
                    assignDate: toDateTimeLocal(row.assignDate),
                  });
                  await loadOptions(); // ensure latest dropdowns & labels
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // grid look (theme-consistent)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  // update
  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    // Build payload according to actionType (omit irrelevant optionals)
    const payload = {
      actionType: String(editValues.actionType || "fuel"),
      currentMileage: Number(editValues.currentMileage),
      previousMileage: Number(editValues.previousMileage),
      carId: Number(editValues.carId),
      userId:
        editValues.userId === "" ||
        editValues.userId === null ||
        editValues.userId === undefined
          ? null
          : Number(editValues.userId),
    };

    if (payload.actionType === "fuel") {
      if (
        editValues.previousFuel !== "" &&
        editValues.previousFuel !== undefined
      ) {
        payload.previousFuel = Number(editValues.previousFuel);
      }
      if (
        editValues.currentFuel !== "" &&
        editValues.currentFuel !== undefined
      ) {
        payload.currentFuel = Number(editValues.currentFuel);
      }
    } else if (payload.actionType === "service") {
      if (
        editValues.serviceCost !== "" &&
        editValues.serviceCost !== undefined &&
        editValues.serviceCost !== null
      ) {
        payload.serviceCost = Number(editValues.serviceCost);
      }
      if (editValues.serviceDate) {
        payload.serviceDate = fromDateTimeLocalToISO(editValues.serviceDate);
      }
      // ensure fuel fields aren't sent
      // ensure assignDate isn't sent
    } else if (payload.actionType === "assign") {
      if (editValues.assignDate) {
        payload.assignDate = fromDateTimeLocalToISO(editValues.assignDate);
      }
      // ensure fuel/service fields aren't sent
    }

    const { data, error } = await putJSON(
      `/api/car-operation/${editRow.id}`,
      payload
    );
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    // Your PUT returns the updated object directly (no nested car/User). Merge minimally.
    const updated = data?.data || data || {};
    const merged = updated?.id
      ? { ...editRow, ...updated }
      : { id: editRow.id, ...payload };

    // optimistic local merge
    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? { ...r, ...merged } : r))
    );

    setToast({
      open: true,
      msg: "Operation updated successfully",
      sev: "success",
    });
    setEditOpen(false);
    setEditRow(null);

    // refresh list & options to restore nested relations (car/User) and latest names
    await Promise.all([load(), loadOptions()]);
  };

  // delete
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { data, error } = await deleteJSON(
      `/api/car-operation/${deleteRow.id}`
    );
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));

    setToast({
      open: true,
      msg: data?.message || "Operation deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);

    await Promise.all([load(), loadOptions()]);
  };

  return (
    <>
      <Box m="20px">
        <Header
          title="CAR OPERATIONS"
          subtitle="View, edit, and manage car operations"
        />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Operation Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <LabelValue label="Action" value={detailRow.actionType} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Car"
                  value={
                    detailRow.car
                      ? `${detailRow.car.carName ?? "-"}${
                          detailRow.car.carPlate
                            ? ` (${detailRow.car.carPlate})`
                            : ""
                        }`
                      : "-"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="User"
                  value={detailRow.User?.fullName ?? "-"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Current Mileage"
                  value={detailRow.currentMileage ?? "-"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Previous Mileage"
                  value={detailRow.previousMileage ?? "-"}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Previous Fuel"
                  value={detailRow.previousFuel ?? "-"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Current Fuel"
                  value={detailRow.currentFuel ?? "-"}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Service Cost"
                  value={detailRow.serviceCost ?? "-"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Service Date"
                  value={fmtDateTime(detailRow.serviceDate)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <LabelValue
                  label="Assign Date"
                  value={fmtDateTime(detailRow.assignDate)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Operation</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="Action Type"
              select
              value={editValues.actionType}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, actionType: e.target.value }))
              }
              fullWidth
              variant="filled"
            >
              <MenuItem value="fuel">fuel</MenuItem>
              <MenuItem value="service">service</MenuItem>
              <MenuItem value="assign">assign</MenuItem>
            </TextField>

            {/* Car dropdown + refresh with dynamic label */}
            <TextField
              label={carLabel}
              select
              value={editValues.carId}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, carId: toNum(e.target.value) }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading || !cars.length}
              helperText={optsLoading ? "Loading cars..." : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh cars">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {ensureOption(cars, editValues.carId).map((c) => (
                <MenuItem key={c.id} value={Number(c.id)}>
                  {c.carName ??
                    c.plateNumber ??
                    c.carPlate ??
                    c.model ??
                    c.title ??
                    `#${c.id}`}
                </MenuItem>
              ))}
            </TextField>

            {/* User dropdown + refresh with dynamic label */}
            <TextField
              label={userLabel}
              select
              value={editValues.userId}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, userId: toNum(e.target.value) }))
              }
              fullWidth
              variant="filled"
              disabled={
                optsLoading || (!users.length && editValues.userId === "")
              }
              helperText={optsLoading ? "Loading users..." : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh users">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {/* allow clearing selection */}
              <MenuItem value="">None</MenuItem>
              {ensureOption(users, editValues.userId).map((u) =>
                u.id ? (
                  <MenuItem key={u.id} value={Number(u.id)}>
                    {u.fullName ?? u.name ?? u.userName ?? `User #${u.id}`}
                  </MenuItem>
                ) : null
              )}
            </TextField>

            {/* Numbers */}
            <TextField
              label="Current Mileage"
              type="number"
              value={editValues.currentMileage}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, currentMileage: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "0.1", min: 0 }}
            />
            <TextField
              label="Previous Mileage"
              type="number"
              value={editValues.previousMileage}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  previousMileage: e.target.value,
                }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "0.1", min: 0 }}
            />

            {/* Fuel (shown for any action, but validated/sent only for fuel) */}
            <TextField
              label="Previous Fuel"
              type="number"
              value={editValues.previousFuel}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, previousFuel: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "0.1", min: 0 }}
            />
            <TextField
              label="Current Fuel"
              type="number"
              value={editValues.currentFuel}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, currentFuel: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "0.1", min: 0 }}
            />

            {/* Service-only */}
            <TextField
              label="Service Cost (Service action)"
              type="number"
              value={editValues.serviceCost}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, serviceCost: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ step: "0.1", min: 0 }}
              sx={{
                display:
                  editValues.actionType === "service" ? undefined : "none",
              }}
            />
            <TextField
              label="Service Date (Service action)"
              type="datetime-local"
              value={editValues.serviceDate}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, serviceDate: e.target.value }))
              }
              fullWidth
              variant="filled"
              InputLabelProps={{ shrink: true }}
              sx={{
                display:
                  editValues.actionType === "service" ? undefined : "none",
              }}
            />

            {/* Assign-only */}
            <TextField
              label="Assign Date (Assign action)"
              type="datetime-local"
              value={editValues.assignDate}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, assignDate: e.target.value }))
              }
              fullWidth
              variant="filled"
              InputLabelProps={{ shrink: true }}
              sx={{
                display:
                  editValues.actionType === "assign" ? undefined : "none",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Operation</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
